// FOC-488 — Cliente DPoP (RFC 9449) para Academy.
// Genera la primera vez un par de llaves ECDSA P-256 con la privada no
// exportable y lo guarda en IndexedDB (`visionbiz-dpop/keys/device`). Firma
// una prueba por cada petición. Si IndexedDB no está disponible, la llave
// vive solo en memoria para esa sesión.
//
// Nada se ejecuta al cargar el módulo: `createStore` y `generateKey` solo
// corren dentro de funciones, para que el build de CRA termine sin errores.

import { createStore, get, set } from 'idb-keyval'

const KEY_ID = 'device'
let store = null
let keyPair = null
let publicJwk = null

const enc = new TextEncoder()

const b64url = (bytes) =>
  btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')

const b64urlJson = (value) => b64url(enc.encode(JSON.stringify(value)))

const generate = () =>
  crypto.subtle.generateKey(
    { name: 'ECDSA', namedCurve: 'P-256' },
    false,
    ['sign', 'verify']
  )

const loadOrCreate = async () => {
  if (!store) store = createStore('visionbiz-dpop', 'keys')
  const existing = await get(KEY_ID, store)
  if (existing) return existing
  const created = await generate()
  await set(KEY_ID, created, store)

  return created
}

export const getKeyPair = () => {
  if (!keyPair) {
    keyPair = (async () => {
      try {
        // Web Locks: dos pestañas abiertas a la vez no crean llaves distintas.
        if (navigator.locks && navigator.locks.request) {
          return await navigator.locks.request('visionbiz-dpop-key', loadOrCreate)
        }

        return await loadOrCreate()
      } catch {
        // Sin IndexedDB (modo privado antiguo): llave solo para esta sesión.
        return generate()
      }
    })()
  }

  return keyPair
}

export const createProof = async (method, url, accessToken) => {
  const { privateKey, publicKey } = await getKeyPair()
  if (!publicJwk) {
    const { kty, crv, x, y } = await crypto.subtle.exportKey('jwk', publicKey)
    publicJwk = { kty, crv, x, y }
  }

  const payload = {
    htm: method.toUpperCase(),
    htu: url,
    iat: Math.floor(Date.now() / 1000),
    jti: crypto.randomUUID()
  }
  if (accessToken) {
    payload.ath = b64url(await crypto.subtle.digest('SHA-256', enc.encode(accessToken)))
  }

  const header = { typ: 'dpop+jwt', alg: 'ES256', jwk: publicJwk }
  const signingInput = `${b64urlJson(header)}.${b64urlJson(payload)}`
  const signature = await crypto.subtle.sign(
    { name: 'ECDSA', hash: 'SHA-256' },
    privateKey,
    enc.encode(signingInput)
  )

  return `${signingInput}.${b64url(signature)}`
}
